import React from 'react';
import {
  Typography,
  Box,
  Grid,
  Paper,
  Chip,
  IconButton,
  Stack,
  Tooltip
} from "@mui/material";
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SchoolIcon from '@mui/icons-material/School';
import WorkIcon from '@mui/icons-material/Work';
import VerifiedIcon from '@mui/icons-material/Verified';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { getMediaUrl } from '../../utils/urlHelper';

const CompactDocumentCard = ({ type, label, url, icon, color = "#2f6bff" }) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 1.5, sm: 1.75 },
      borderRadius: '16px',
      border: '1px solid #eef0f4',
      transition: 'all 0.2s ease',
      '&:hover': {
        borderColor: `${color}40`,
        transform: 'translateY(-2px)',
        boxShadow: `0 6px 18px ${color}14`
      }
    }}
  >
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{
          width: 38,
          height: 38,
          borderRadius: '12px',
          backgroundColor: `${color}14`,
          color: color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 38,
        }}>
          {React.cloneElement(icon, { sx: { fontSize: 17 } })}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: '#1e293b',
              fontSize: '0.825rem',
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {label}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: '#94a3b8',
              fontSize: '0.65rem',
              display: 'block'
            }}
          >
            {type}
          </Typography>
        </Box>
      </Stack>
      <Stack direction="row" spacing={0.5}>
        <Tooltip title="View">
          <IconButton
            size="small"
            component="a"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              width: 30,
              height: 30,
              color: color,
              '&:hover': {
                backgroundColor: `${color}15`
              }
            }}
          >
            <VisibilityIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Download">
          <IconButton
            size="small"
            component="a"
            href={url}
            download
            sx={{
              width: 30,
              height: 30,
              color: color,
              '&:hover': {
                backgroundColor: `${color}15`
              }
            }}
          >
            <DownloadIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Stack>
    </Stack>
  </Paper>
);

const EmpDcs = ({ data }) => {
  const documentTypes = [
    {
      key: 'aadhar_card',
      label: 'Aadhar Card',
      type: 'Identity',
      icon: <CreditCardIcon />,
      color: '#2f6bff'
    },
    {
      key: 'pan_card',
      label: 'PAN Card',
      type: 'Tax Document',
      icon: <CreditCardIcon />,
      color: '#2f6bff'
    },
    {
      key: 'bank_proof',
      label: 'Bank Proof',
      type: 'Financial',
      icon: <AccountBalanceIcon />,
      color: '#2f6bff'
    },
    {
      key: 'education_certificate',
      label: 'Education Certificate',
      type: 'Academic',
      icon: <SchoolIcon />,
      color: '#2f6bff'
    },
    {
      key: 'experience_letter',
      label: 'Experience Letter',
      type: 'Professional',
      icon: <WorkIcon />,
      color: '#2f6bff'
    },
    {
      key: 'nicsm_certificate',
      label: 'NICSM Certificate',
      type: 'Certification',
      icon: <VerifiedIcon />,
      color: '#2f6bff'
    }
  ];

  const availableDocuments = data?.length > 0 ?
    documentTypes.filter(docType =>
      data.some(doc => doc[docType.key])
    ).map(docType => {
      const doc = data.find(d => d[docType.key]);
      return {
        ...docType,
        url: getMediaUrl(doc[docType.key])
      };
    }) : [];

  return (
    <Box>
      {availableDocuments.length > 0 ? (
        <>
          <Grid container spacing={1.5}>
            {availableDocuments.map((doc, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <CompactDocumentCard {...doc} />
              </Grid>
            ))}
          </Grid>

          {/* Document Count Badge */}
          <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
            <Chip
              icon={<InsertDriveFileIcon sx={{ fontSize: 15 }} />}
              label={`${availableDocuments.length} Document${availableDocuments.length > 1 ? 's' : ''}`}
              size="small"
              color="primary"
              variant="outlined"
              sx={{
                fontWeight: 600,
                fontSize: '0.7rem',
                height: 26
              }}
            />
            <Chip
              icon={<VerifiedIcon sx={{ fontSize: 15 }} />}
              label="Verified"
              size="small"
              color="success"
              sx={{
                fontWeight: 600,
                fontSize: '0.7rem',
                height: 26
              }}
            />
          </Box>
        </>
      ) : (
        // Compact Empty State
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: '18px',
            border: '1px solid #eef0f4',
            textAlign: 'center',
            backgroundColor: '#f8fafc'
          }}
        >
          <FolderIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1.5 }} />
          <Typography variant="subtitle2" sx={{ color: '#64748b', mb: 0.5, fontWeight: 600 }}>
            No Documents Available
          </Typography>
          <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.75rem' }}>
            Documents will appear here once uploaded
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default EmpDcs;